import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RequestBodyOption } from 'openapi-fetch';
import { FilterKeys, MediaType, ResponseObjectMap, SuccessResponse } from 'openapi-typescript-helpers';
import { paths } from 'src/app/core/services/api/generated/ix-api.types.d';
import { OverloadedParameters } from 'src/app/shared/models/type-helper';

type KeysOfValue<T, V extends T[keyof T]> = { [K in keyof T]-?: T[K] extends V ? K : never }[keyof T];

// All api resources
type DeleteResource = KeysOfValue<paths, { delete }>;
type GetResource = KeysOfValue<paths, { get }>;
type PatchResource = KeysOfValue<paths, { patch }>;
type PostResource = KeysOfValue<paths, { post }>;
type PutResource = KeysOfValue<paths, { put }>;

// All operation objects
export type AnyApiPath = keyof paths;
type ApiPaths<T extends AnyApiPath> = paths[T];
type DeleteOperation<T extends DeleteResource> = ApiPaths<T extends DeleteResource ? T : never>['delete'];
type GetOperation<T extends GetResource> = ApiPaths<T extends GetResource ? T : never>['get'];
type PatchOperation<T extends PatchResource> = ApiPaths<T extends PatchResource ? T : never>['patch'];
type PostOperation<T extends PostResource> = ApiPaths<T extends PostResource ? T : never>['post'];
type PutOperation<T extends PutResource> = ApiPaths<T extends PutResource ? T : never>['put'];

// Success responses
type Response<T> = FilterKeys<SuccessResponse<ResponseObjectMap<T>>, MediaType>;

// Angular HttpClient options
type DeleteOptions = Omit<
  NonNullable<OverloadedParameters<typeof HttpClient.prototype.delete>[1]>,
  'observe' | 'responseType'
>;
type GetOptions = Omit<
  NonNullable<OverloadedParameters<typeof HttpClient.prototype.get>[1]>,
  'observe' | 'responseType'
>;
type PatchOptions = Omit<
  NonNullable<OverloadedParameters<typeof HttpClient.prototype.patch>[2]>,
  'observe' | 'responseType'
>;
type PostOptions = Omit<
  NonNullable<OverloadedParameters<typeof HttpClient.prototype.post>[2]>,
  'observe' | 'responseType'
>;
type PutOptions = Omit<
  NonNullable<OverloadedParameters<typeof HttpClient.prototype.put>[2]>,
  'observe' | 'responseType'
>;

@Injectable({ providedIn: 'root' })
export class IxRestApi {
  readonly #http = inject(HttpClient);

  delete<T extends DeleteResource>(resource: Resource<T>, options?: DeleteOptions) {
    return this.#http.delete<Response<DeleteOperation<T>>>(createFinalUrl(resource), options);
  }

  get<T extends GetResource>(resource: Resource<T>, options?: GetOptions) {
    return this.#http.get<Response<GetOperation<T>>>(createFinalUrl(resource), options);
  }

  patch<T extends PatchResource, Operation extends PatchOperation<T>>(
    resource: Resource<T>,
    body: RequestBodyOption<Operation>['body'] extends never
      ? undefined
      : RequestBodyOption<Operation>['body'],
    options?: PatchOptions,
  ) {
    return this.#http.patch<Response<Operation>>(createFinalUrl(resource), body, options);
  }

  post<T extends PostResource, Operation extends PostOperation<T>>(
    resource: Resource<T>,
    body: RequestBodyOption<Operation>['body'] extends never
      ? undefined
      : RequestBodyOption<Operation>['body'],
    options?: PostOptions,
  ) {
    return this.#http.post<Response<PostOperation<T>>>(createFinalUrl(resource), body, options);
  }

  put<T extends PutResource, Operation extends PutOperation<T>>(
    resource: Resource<T>,
    body: RequestBodyOption<Operation>['body'] extends never
      ? undefined
      : RequestBodyOption<Operation>['body'],
    options?: PutOptions,
  ) {
    return this.#http.put<Response<Operation>>(createFinalUrl(resource), body, options);
  }
}

interface Resource<T> {
  pathname: T;
  options: {
    baseUrl?: string;
    path?: Record<string, unknown>;
  };
}
/**
 * Based on {@link https://github.com/drwpow/openapi-typescript/blob/aaca108ba6fbcec7c0a77923d3825e60d9fdedf8/packages/openapi-fetch/src/index.js#L231-L243}
 * Because we want to use Angular HttpClient we don't want to serialize or add query params here, we only want to translate the path ids.
 */
export function createFinalUrl<T extends string>(resource: Resource<T>): string {
  let options = { baseUrl: '', ...resource.options };

  let finalURL = `${options.baseUrl}${resource.pathname}`;
  if (options.path) {
    for (const [k, v] of Object.entries(options.path)) {
      finalURL = finalURL.replace(`{${k}}`, encodeURIComponent(String(v)));
    }
  }
  return finalURL;
}

export function urlToRegExp(url: AnyApiPath): RegExp {
  return new RegExp(url.replace(/\/\{[\w]+\}/g, '/[\\w-]+').concat('$'));
}
